import {
  Button,
  FormControl,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Radio,
  RadioGroup,
  Text,
  VStack,
} from "@chakra-ui/react";
import debounce from "debounce";
import { useState } from "react";
import { useMutation, useQueryClient } from "react-query";
import { useDispatch } from "react-redux";
import { markPreviousAsRead } from "../../../services/readingOrdersApi";
import { trackEvent } from "../../../shared/analytics";
import { searchComics } from "../../../services/searchApi";
import Autocomplete from "../../../shared/components/Autocomplete";
import {
  markPreviousAsRead as markPreviousAsReadAction,
  markPreviousAsReadLater,
  markPreviousAsSkipped,
  setOrderPosition,
} from "../actions";

const JumpToModal = ({ isOpen, onClose, reading }) => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const [selectedComic, setSelectedComic] = useState(null);
  const [selectedAction, setSelectedAction] = useState(1);
  const readingOrderId = reading?.readingOrder?.id;
  const comicSuggestionsFn = debounce(
    (keyword) => searchComics(keyword, readingOrderId),
    700
  );

  const markAllPreviousMutation = useMutation(
    ({ order, readingId, read, skipped, readLater }) => {
      return markPreviousAsRead(order, readingId, read, skipped, readLater);
    },
    {
      onSuccess: (response, variables) => {
        queryClient.setQueryData(
          ["reading", variables.readingId],
          response.data
        );

        dispatch(setOrderPosition(variables.readingId, variables.order));
        if (variables.read) {
          dispatch(
            markPreviousAsReadAction(variables.readingId, variables.order)
          );
        }
        if (variables.readLater) {
          dispatch(
            markPreviousAsReadLater(variables.readingId, variables.order)
          );
        }
        if (variables.skipped) {
          dispatch(markPreviousAsSkipped(variables.readingId, variables.order));
        }

        queryClient.invalidateQueries(["readingOrder", variables.readingId]);
        queryClient.invalidateQueries("paywall");
      },
    }
  );

  const onSubmit = () => {
    trackEvent("jump_to_comic", {
      reading_id: reading.id,
      reading_order_id: readingOrderId,
      position: selectedComic.order,
      previous_comics_action: ["leave", "read", "skipped", "read_later"][
        selectedAction - 1
      ],
    });
    markAllPreviousMutation
      .mutateAsync({
        order: selectedComic.order,
        readingId: reading.id,
        read: selectedAction === 2,
        skipped: selectedAction === 3,
        readLater: selectedAction === 4,
      })
      .then(() => {
        onClose();
        window.location.reload();
      });
  };

  if (!readingOrderId) {
    return null;
  }

  return (
    <Modal onClose={onClose} isOpen={isOpen} isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Jump to a specific comic</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4}>
            <FormControl id="comic" isRequired>
              <Autocomplete
                suggestionsFn={comicSuggestionsFn}
                onSelection={setSelectedComic}
                readingOrderId={readingOrderId}
              />
            </FormControl>
            <FormControl isRequired>
              <RadioGroup
                value={parseInt(selectedAction, 10)}
                onChange={(action) => {
                  setSelectedAction(parseInt(action, 10));
                }}
              >
                <Radio value={1} tabIndex={1}>
                  Continue reading from that point
                </Radio>
                <Text my="4">Or mark previous comics as:</Text>
                <VStack alignItems="flex-start">
                  <Radio value={2} tabIndex={2}>
                    Read
                  </Radio>
                  <Radio value={3} tabIndex={3}>
                    Skipped
                  </Radio>
                  <Radio value={4} tabIndex={4}>
                    Read later
                  </Radio>
                </VStack>
              </RadioGroup>
            </FormControl>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button tabIndex={6} onClick={onClose}>
            Cancel
          </Button>
          <Button
            colorScheme="red"
            color="white"
            tabIndex={5}
            ml="3"
            onClick={onSubmit}
            disabled={markAllPreviousMutation.isLoading}
          >
            Make the jump
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default JumpToModal;
